// Client-facing sponsor-ad report, opened from a share link
// (`/share/ads/{token}`). Rendered OUTSIDE the auth gate (see App.js)
// and talks to the one public endpoint, `GET /api/share/ads/{token}`.
//
// View-only by construction: there is no refresh, diagnose, delete or
// edit affordance here, and the endpoint it calls offers none either.
// The numbers are whatever the nightly 03:00 refresh last cached.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  BarChart3, Heart, MessageSquare, Share2, Eye, ExternalLink, Loader2,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

async function fetchReport(token, range) {
  const params = new URLSearchParams();
  if (range?.since) params.set("since", range.since);
  if (range?.until) params.set("until", range.until);
  const qs = params.toString();
  const url = `${BASE}/api/share/ads/${encodeURIComponent(token)}${qs ? `?${qs}` : ""}`;
  // `credentials: "omit"` — this page must work with no session at all,
  // and there is no reason to send an editor's cookie along if one
  // happens to exist in the same browser.
  const res = await fetch(url, { credentials: "omit", cache: "no-store" });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.detail || "";
    } catch {
      /* non-JSON body */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString();
}

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

function Stat({ label, value, icon: Icon, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[var(--border)] p-3"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className="font-serif-display text-2xl mt-1 leading-none">
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

export default function PublicAdReportPage() {
  const { token } = useParams();
  const [pendingSince, setPendingSince] = useState("");
  const [pendingUntil, setPendingUntil] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = since || until ? { since, until } : null;
  const dirty = pendingSince !== since || pendingUntil !== until;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchReport(token, range)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `range` is derived from since/until; listing those keeps the
    // dependency array stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, since, until]);

  useEffect(() => {
    document.title = data?.ad_name
      ? `${data.ad_name} — sponsored post report`
      : "Sponsored post report";
  }, [data]);

  const applyRange = () => {
    setSince(pendingSince);
    setUntil(pendingUntil);
  };
  const clearRange = () => {
    setPendingSince("");
    setPendingUntil("");
    setSince("");
    setUntil("");
  };

  if (error && !data) {
    const gone = error.status === 404;
    return (
      <div
        data-testid="share-report-error"
        className="min-h-screen w-full flex items-center justify-center bg-[var(--bg)] px-6"
      >
        <div className="max-w-md text-center">
          <div className="overline mb-2">Sponsored post report</div>
          <h1 className="font-serif-display text-2xl mb-3">
            {gone ? "This link is no longer valid." : "Couldn't load this report."}
          </h1>
          <p className="text-sm text-[var(--ink-muted)]">
            {gone
              ? "The link may have expired or been replaced. Ask your contact at the publication for a new one."
              : error.message}
          </p>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {
    posts: 0, reactions: 0, comments: 0, shares: 0, impressions: 0,
  };
  const placements = data?.placements || [];

  return (
    <div
      data-testid="share-report"
      className="min-h-screen w-full bg-[var(--bg)] overflow-y-auto"
    >
      <div className="border-b border-[var(--border)] bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
          <div className="overline mb-1">
            {data?.property_name || "Sponsored post report"}
          </div>
          <h1 className="font-serif-display text-3xl leading-tight">
            {data?.ad_name || "Sponsored posts"}
          </h1>
          <p className="text-xs text-[var(--ink-muted)] mt-2 font-mono-display">
            Facebook performance of every post that carried this ad.
            {data?.metrics_updated_at
              ? ` Updated nightly · last updated ${fmtDateTime(data.metrics_updated_at)}.`
              : " Updated nightly."}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono-display mb-4">
          <span className="uppercase tracking-wider text-[var(--ink-muted)]">
            Date range
          </span>
          <input
            type="date"
            data-testid="share-report-since"
            value={pendingSince}
            onChange={(e) => setPendingSince(e.target.value)}
            max={pendingUntil || undefined}
            className="border border-[var(--border)] bg-white px-2 py-1 text-xs"
            aria-label="From date"
          />
          <span className="text-[var(--ink-muted)]">→</span>
          <input
            type="date"
            data-testid="share-report-until"
            value={pendingUntil}
            onChange={(e) => setPendingUntil(e.target.value)}
            min={pendingSince || undefined}
            className="border border-[var(--border)] bg-white px-2 py-1 text-xs"
            aria-label="To date"
          />
          <button
            type="button"
            data-testid="share-report-apply-range"
            onClick={applyRange}
            disabled={!dirty}
            className="px-3 py-1 text-[10px] uppercase tracking-wider bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Apply
          </button>
          {(since || until || pendingSince || pendingUntil) && (
            <button
              type="button"
              data-testid="share-report-clear-range"
              onClick={clearRange}
              className="text-[10px] uppercase tracking-wider text-[var(--accent)] hover:underline"
            >
              Clear
            </button>
          )}
          {loading && <Loader2 className="w-3 h-3 animate-spin ml-auto" />}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
          <Stat label="Posts" value={totals.posts} icon={BarChart3} testId="share-stat-posts" />
          <Stat label="Reactions" value={totals.reactions} icon={Heart} testId="share-stat-reactions" />
          <Stat label="Comments" value={totals.comments} icon={MessageSquare} testId="share-stat-comments" />
          <Stat label="Shares" value={totals.shares} icon={Share2} testId="share-stat-shares" />
          <Stat label="Views" value={totals.impressions} icon={Eye} testId="share-stat-impressions" />
        </div>

        {!loading && placements.length === 0 ? (
          <div className="text-sm text-[var(--ink-muted)]">
            {since || until
              ? "No posts in this date range."
              : "No posts have carried this ad yet."}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {placements.map((p, i) => (
              <li
                key={`${p.published_at}-${i}`}
                data-testid="share-report-row"
                className="flex items-center gap-3 bg-white border border-[var(--border)] px-3 py-2 text-xs"
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">
                    {p.article_headline || "(no headline)"}
                  </div>
                  <div className="text-[10px] text-[var(--ink-muted)] font-mono-display truncate">
                    {p.page_name}
                    {p.page_name ? " · " : ""}
                    {fmtDate(p.published_at)}
                    {p.stale && (
                      <span className="ml-2 text-amber-600">
                        (figures may be out of date)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 font-mono-display text-[11px]">
                  <span className="inline-flex items-center gap-1" title="Reactions">
                    <Heart className="w-3 h-3" />
                    {p.reactions}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Comments">
                    <MessageSquare className="w-3 h-3" />
                    {p.comments}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Shares">
                    <Share2 className="w-3 h-3" />
                    {p.shares}
                  </span>
                  <span
                    className="inline-flex items-center gap-1"
                    title={p.impressions == null ? "Views not available for this post" : "Views on Facebook"}
                  >
                    <Eye className="w-3 h-3" />
                    {p.impressions == null ? "—" : p.impressions}
                  </span>
                </div>
                {p.permalink_url && (
                  <a
                    href={p.permalink_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--ink-muted)] hover:text-[var(--accent)]"
                    aria-label="Open post on Facebook"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}

        <p className="text-[10px] text-[var(--ink-muted)] font-mono-display mt-6">
          Per-post figures are lifetime totals from Facebook; the date
          range only chooses which posts are counted.
        </p>
      </div>
    </div>
  );
}